<script setup>
import { getCookie } from "@/helpers/cookie";
import { useStore } from "@/store/store";
import { computed, onMounted, ref, watch } from "vue";
import { countryList } from "@/constants/countries";

const store = useStore();

defineProps({
  size: {
    type: String,
    default: "md",
  },
});

// Flag to track if language was manually set by user
const isManuallySet = ref(false);

const languageList = computed(() => {
  let langArr = store.state.account.languages;

  let resultArr = [];

  for (var i = 0; i < langArr.length; i++) {
    let obj = {
      text: langArr[i].attributes.name,
      value: langArr[i].attributes.code,
    };

    resultArr.push(obj);
  }

  return resultArr;
});

const language = computed({
  get() {
    return store.state.translation.locate;
  },
  set(newValue) {
    isManuallySet.value = true;
    store.dispatch("setTranslationPermanent", newValue);
  },
});

const getClientLanguage = () => {
  console.debug("[LanguageSelect] Starting language detection process");

  // 1. Check cookie first (highest priority)
  const cookieLang = getCookie("language");
  console.debug("[LanguageSelect] Cookie check:", {
    cookieLang: cookieLang || "none",
    isValid: !!(cookieLang && cookieLang !== "undefined" && cookieLang !== "null"),
  });

  if (cookieLang && cookieLang !== "undefined" && cookieLang !== "null") {
    console.debug("[LanguageSelect] Language selection: using cookie", {
      cookieLang,
      reason: "Valid cookie found - highest priority",
    });
    return cookieLang;
  }

  // 2. Check IP-based country from headers (stored in account.userCountry)
  const userCountry = store.state.account.userCountry;
  console.debug("[LanguageSelect] User country check:", {
    userCountry: userCountry || "none",
  });

  let countryBasedLang = null;
  if (userCountry) {
    // Find the country in the list
    const countryData = countryList.find(
      (v) => v.iso_code.toLocaleLowerCase() === userCountry.toLocaleLowerCase()
    );

    console.debug("[LanguageSelect] Country mapping:", {
      countryCode: userCountry,
      countryFound: !!countryData,
      languageCode: countryData?.iso_639_1 || "none",
    });

    if (countryData && countryData.iso_639_1) {
      countryBasedLang = countryData.iso_639_1.toLocaleLowerCase();
      console.debug("[LanguageSelect] Language selection: using country", {
        countryBasedLang,
        reason: "No valid cookie, using country-based detection",
      });
      return countryBasedLang;
    }
  }

  // 3. Fall back to browser language
  const [browserLang] = (window.navigator.language || window.navigator.userLanguage || "en").split(
    "-"
  );
  console.debug("[LanguageSelect] Language selection: using browser fallback", {
    browserLang: browserLang.toLowerCase(),
    reason: "No cookie or country available, using browser language",
  });
  return browserLang.toLowerCase();
};

const setLanguageIfAvailable = () => {
  // Don't override if user manually selected a language
  if (isManuallySet.value) {
    console.debug("[LanguageSelect] Skipping auto-detection: user manually set language");
    return;
  }

  // Wait for languages to be loaded
  if (!languageList.value || languageList.value.length === 0) {
    console.debug("[LanguageSelect] Waiting for languages to load...");
    return;
  }

  const detectedLang = getClientLanguage();

  // Check if the detected language is available in the organization's language list (case-insensitive)
  const isLangAvailable = languageList.value.some(
    (lang) => lang.value.toLowerCase() === detectedLang.toLowerCase()
  );

  // Find the actual language code from the list (with correct case)
  const actualLangCode = languageList.value.find(
    (lang) => lang.value.toLowerCase() === detectedLang.toLowerCase()
  )?.value;

  console.debug("[LanguageSelect] Language availability check:", {
    detectedLang,
    actualLangCode: actualLangCode || "none",
    isAvailable: isLangAvailable,
    currentLang: language.value,
    availableLanguages: languageList.value.map((lang) => lang.value),
    userCountry: store.state.account.userCountry || "none",
  });

  if (isLangAvailable && actualLangCode && language.value !== actualLangCode) {
    console.debug("[LanguageSelect] Setting language:", {
      from: language.value,
      to: actualLangCode,
      reason: "Detected language is available",
    });
    language.value = actualLangCode;
  } else if (!isLangAvailable && language.value !== "en") {
    console.debug("[LanguageSelect] Setting default language:", {
      from: language.value,
      to: "en",
      reason: "Detected language not available, defaulting to English",
    });
    // Default to English if detected language is not available
    language.value = "en";
  } else {
    console.debug("[LanguageSelect] No language change needed:", {
      currentLang: language.value,
      detectedLang,
      reason: isLangAvailable ? "Already using correct language" : "Already using default",
    });
  }
};

// Watch for userCountry changes (in case it's loaded after component mounts)
watch(
  () => store.state.account.userCountry,
  (newCountry) => {
    console.debug("[LanguageSelect] User country changed:", {
      newCountry: newCountry || "none",
      hasCookie: !!getCookie("language"),
    });
    if (newCountry && !getCookie("language")) {
      setLanguageIfAvailable();
    }
  }
);

// Watch for languages list changes (in case it's loaded after component mounts)
watch(
  () => store.state.account.languages,
  () => {
    console.debug("[LanguageSelect] Languages list updated:", {
      languageCount: languageList.value.length,
      hasCookie: !!getCookie("language"),
    });
    if (languageList.value.length > 0 && !getCookie("language")) {
      setLanguageIfAvailable();
    }
  }
);

onMounted(() => {
  console.debug("[LanguageSelect] Component mounted, initializing language detection");
  setLanguageIfAvailable();
});
</script>

<template>
  <b-form-select
    id="language-select"
    v-model="language"
    :options="languageList"
    :size="size"
    class="tw-font-sans tw-cursor-pointer"
  />
</template>
